import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, catchError, take, tap, throwError } from 'rxjs';
import { saveAs } from 'file-saver';
import { ErrorService } from '../services/error.service';

export interface ApiRequestOptions {
  userVisibleErrors?: boolean;
}

export interface ApiMutationOptions extends ApiRequestOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
}

export type ApiGetParams = HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;

export interface ApiBlobMutationOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly errorService = inject(ErrorService);

  private saveBlob(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  get<T>(
    url: string,
    params?: ApiGetParams,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return this.withErrorHandling(this.http.get<T>(url, { params }), options);
  }

  post<T>(url: string, body: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.withErrorHandling(this.http.post<T>(url, body), options);
  }

  downloadResponse(url: string, params?: ApiGetParams): Observable<HttpResponse<Blob>> {
    return this.http.get(url, {
      observe: 'response',
      params,
      responseType: 'blob',
    }).pipe(
      take(1),
    );
  }

  getBlobResponse(url: string, params?: ApiGetParams): Observable<HttpResponse<Blob>> {
    return this.downloadResponse(url, params);
  }

  postBlobResponse(
    url: string,
    body: unknown,
    options?: ApiBlobMutationOptions,
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(url, body, {
      headers: options?.headers,
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      take(1),
    );
  }

  put<T>(url: string, body: unknown, options?: ApiMutationOptions): Observable<T> {
    const request$ = this.http.put(url, body, {
      headers: options?.headers,
    }) as Observable<T>;

    return this.withErrorHandling(request$, options);
  }

  patch<T>(url: string, body: unknown): Observable<T> {
    return this.withErrorHandling(this.http.patch<T>(url, body));
  }

  delete<T>(url: string): Observable<T> {
    return this.withErrorHandling(this.http.delete<T>(url));
  }

  download(url: string, params?: ApiGetParams): Observable<Blob> {
    return this.withErrorHandling(this.http.get(url, {
      params,
      responseType: 'blob',
    }));
  }

  upload<T>(url: string, formData: FormData): Observable<T> {
    return this.withErrorHandling(this.http.post<T>(url, formData));
  }

  downloadAndSave(url: string, filename: string, params?: ApiGetParams): Observable<Blob> {
    return this.download(url, params).pipe(
      tap(blob => this.saveBlob(blob, filename))
    );
  }

  uploadFile<T>(url: string, file: File, fieldName = 'file'): Observable<T> {
    const fd = new FormData();
    fd.append(fieldName, file);
    return this.upload<T>(url, fd);
  }

  private withErrorHandling<T>(source$: Observable<T>, options?: ApiRequestOptions): Observable<T> {
    return source$.pipe(
      take(1),
      catchError((error) => {
        if (options?.userVisibleErrors === false) {
          return throwError(() => this.errorService.normalize(error));
        }

        return this.errorService.handle(error);
      }),
    );
  }
}
